import {useEffect, useState } from 'react';
import {
  Box, Grid, Typography, Card, Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from "../contexts/SnackBarContext";
import { VisibilityOutlined } from '@mui/icons-material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import SelezioneComanda from '../pages/SelezionaComande';
import ModalEditTavolo from '../pages/ModalEditTavolo';
import { getAllOrdini } from '../APIs/OrdersAPI';
import { eliminaTavolo } from '../APIs/TablesAPI';
import { getTenantTranslation } from '../utils/getTenantTranslation';

const VistaStandard = ({
  tavoliVisibili,
  getColor,
  editTable,
  onCanOpenTableOrder,
  onOpenSpaPlanner,
  onTablesChanged,
}) => {
  const [selectedTavolo, setSelectedTavolo] = useState(null);
  const [openComanda, setOpenComanda] = useState(false);
  const [openEditTable, setOpenEditTable] = useState([false, null]);
  const [openDeleteTable, setOpenDeleteTable] = useState([false, null]);
  const [comande, setComande] = useState([]);
  const { t } = useTranslation();
  const theme = useTheme();
  const tenantType = localStorage.getItem('tenantType');
  const isSpaTenant = String(tenantType || '').toUpperCase() === 'SPA';
  const { showSnackbar } = useSnackbar();
  const spaUnavailableLabel = (status) => {
    const normalized = String(status || '').toUpperCase();
    const labels = {
      CLEANING: t('spa.dashboard.cabinStatuses.CLEANING', { defaultValue: 'Pulizia' }),
      MAINTENANCE: t('spa.dashboard.cabinStatuses.MAINTENANCE', { defaultValue: 'Manutenzione' }),
      OUT_OF_SERVICE: t('spa.dashboard.cabinStatuses.OUT_OF_SERVICE', { defaultValue: 'Fuori servizio' }),
    };
    return labels[normalized] || t('NON_DISPONIBILE');
  };

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');

    const fetchOrders = async () => {
      try {
        const dataOrdini = await getAllOrdini(token, tenant, { noCache: true });
        const ordiniPerTavolo = {};
        dataOrdini.forEach((ordine) => {
          if (ordine.status !== 'OPEN') return;
          const tableId = ordine.tableId;
          if (!ordiniPerTavolo[tableId]) {
            ordiniPerTavolo[tableId] = {
              ...ordine,
              orderProducts: [...(ordine.orderProducts || [])]
            };
          } else {
            ordiniPerTavolo[tableId].orderProducts.push(...(ordine.orderProducts || []));
          }
        });
        setComande(Object.values(ordiniPerTavolo));
      } catch (err) {}
    };
    fetchOrders();
  }, []);

  const handleElimina = async (tavId) => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    try {
      await eliminaTavolo(tavId, token, tenant);
      window.dispatchEvent(new CustomEvent('tavoloEliminato', { detail: { id: tavId } }));
    } catch (error) {
      showSnackbar(t("reservations_details.error") + error.message, "error");
    }
  };

  return (
    <>
      <Box
        sx={{
          pr: 0.5,
          touchAction: 'pan-y pinch-zoom',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Grid
          container
          columnSpacing={2}
          rowSpacing={2}
          sx={{
            flexWrap: 'wrap',
            justifyContent: 'center',
            minHeight: '480px',
            touchAction: 'pan-y pinch-zoom',
          }}
        >
          {tavoliVisibili.map((tavolo) => (
            <Grid item xs={6} sm={4} md={2} lg={2} xl={2} key={tavolo.id}>
            <Card
              sx={{
                p: 2,
                position: "relative",
                color: theme.palette.primary.contrastText,
                textAlign: 'center',
                backgroundColor: getColor(tavolo.status),
                cursor: 'pointer',
                borderRadius: '8px',
              }}
            >
              {/* Edit / Delete buttons */}
              {editTable && (
                <IconButton
                  onClick={() => setOpenEditTable([true, tavolo])}
                  sx={{
                    position: "absolute", top: "0%", left: "0%",
                    ...theme.mappatavoli.add_edit_button
                  }}
                >
                  <ModeEditOutlineOutlinedIcon />
                </IconButton>
              )}
              {editTable && (
                <IconButton
                  onClick={() => setOpenDeleteTable([true, tavolo.id])}
                  sx={{
                    position: "absolute", top: "0%", right: "0%",
                    ...theme.mappatavoli.delete_button
                  }}
                >
                  <DeleteForeverOutlinedIcon />
                </IconButton>
              )}

              {/* Table name */}
              <Typography variant="h6">{tavolo.name}</Typography>

              {/* Status line */}
              <Typography variant="body1">
                {t('admin.tenants.table.status')}: {t(tavolo.status)}
                {tavolo.status === 'RISERVATO' && tavolo.oraPrenotazione !== '—'
                  ? ` (${tavolo.oraPrenotazione})`
                  : ''}
              </Typography>

              {/* Seats / SPA non-availability reason */}
              <Typography
                variant="caption"
                sx={{
                  textAlign: "center",
                  fontFamily: "Helvetica Neue",
                  fontSize: "14px",
                  lineHeight: "175%",
                  letterSpacing: "0.217px",
                }}
              >
                {isSpaTenant && tavolo.status === "NON_DISPONIBILE"
                  ? `${t('admin.tenants.table.status')}: ${spaUnavailableLabel(tavolo.spaCabinStatus)}`
                  : tavolo.status === "LIBERO"
                  ? (getTenantTranslation(t, tenantType, "seatNumber") + ": " + tavolo.maxSeats)
                  : (getTenantTranslation(t, tenantType, "occupiedSeats") + ": " + tavolo.takenSeats + "/" + tavolo.maxSeats)}
              </Typography>

              {tavolo.note && (
                <Typography
                  variant="caption"
                  title={tavolo.note}
                  sx={{ mt: 0.5, px: 1, fontStyle: 'italic', opacity: 0.9, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {t('notes')}: {tavolo.note}
                </Typography>
              )}

              {/* ── Reservation info pill — only when reserved */}
              {tavolo.status !== "LIBERO" && tavolo.hasPrenotazione && (
                <Box
                  sx={{
                    mt: 1,
                    px: 1,
                    py: 0.5,
                    borderRadius: '6px',
                    backgroundColor: 'rgba(0,0,0,0.20)',
                    textAlign: 'left',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "12px", fontWeight: 700, display: 'block', lineHeight: 1.5 }}
                  >
                    👤 {tavolo.nomeCliente}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "12px", display: 'block', lineHeight: 1.4 }}
                  >
                    🕐 {tavolo.oraPrenotazione}
                    {tavolo.numeroOspiti !== '—' && ` · ${t('guests')}: ${tavolo.numeroOspiti}`}
                  </Typography>
                  {tavolo.notePrenotazione && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "11px",
                        display: 'block',
                        lineHeight: 1.3,
                        opacity: 0.85,
                        mt: 0.25,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      }}
                    >
                      📝 {tavolo.notePrenotazione}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Action button */}
              <Button
                disabled={tavolo.status === "NON_DISPONIBILE"}
                variant='contained'
                sx={{
                  display: 'flex',
                  width: '100%',
                  mt: 1,
                  padding: '8px 0',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  alignSelf: 'stretch',
                  borderRadius: '4px',
                  color: theme.palette.text.primary,
                  background: theme.palette.mode === 'light' ? '#E9F3FF' : '#192F49',
                }}
                onClick={async () => {
                  if (isSpaTenant && onOpenSpaPlanner) {
                    onOpenSpaPlanner(tavolo);
                    return;
                  }
                  if (onCanOpenTableOrder) {
                    const canOpen = await onCanOpenTableOrder(tavolo);
                    if (!canOpen) return;
                  }
                  setSelectedTavolo(tavolo);
                  setOpenComanda(true);
                }}
              >
                {isSpaTenant ? t("spa.planner.newAppointment") : getTenantTranslation(t, tenantType, "newOrder")}
                <VisibilityOutlined />
              </Button>
            </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {openComanda && (
        <Dialog
          open={openComanda}
          fullWidth
          maxWidth={false}
          PaperProps={{
            sx: {
              width: { xs: '100vw', md: '95vw' },
              maxWidth: '1800px',
              height: { xs: '100dvh', md: 'auto' },
              maxHeight: { xs: '100dvh', md: 'calc(100dvh - 32px)' },
              m: { xs: 0, md: 2 },
              borderRadius: { xs: 0, md: 2 },
              overflow: 'hidden',
            },
          }}
          onClose={() => setOpenComanda(false)}
        >
          <DialogContent sx={{
            p: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'hidden',
            overflowX: 'hidden',
            touchAction: 'pan-x pan-y pinch-zoom',
            WebkitOverflowScrolling: 'touch',
          }}>
            <SelezioneComanda
              open={openComanda}
              tavolo={selectedTavolo}
              onClose={() => setOpenComanda(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {openEditTable && (
        <ModalEditTavolo
          open={openEditTable[0]}
          tavolo={openEditTable[1]}
          onSuccess={onTablesChanged}
          onClose={() => setOpenEditTable([false, null])}
        />
      )}

      {openDeleteTable[0] && (
        <Dialog
          open={openDeleteTable}
          width={"50%"}
          onClose={() => setOpenDeleteTable([false, null])}
        >
          <DialogTitle>
            <Typography variant='h5'>
              {getTenantTranslation(t, tenantType, 'deleteTableRequestTitle').toUpperCase()}
            </Typography>
            <Divider />
          </DialogTitle>
          <DialogContent sx={{ p: 2 }}>
            <Typography sx={{ fontSize: 24, textAlign: "center" }}>
              {getTenantTranslation(t, tenantType, 'deleteTableRequest')}
            </Typography>
            <Box display={"flex"} justifyContent={'center'} sx={{ mt: 2 }} gap={3}>
              <Button
                variant='contained'
                onClick={() => {
                  handleElimina(openDeleteTable[1]).then(() => setOpenDeleteTable([false, null]));
                }}
                sx={{ width: "45%" }}
              >
                {t("confirm")}
              </Button>
              <Button
                variant='outlined'
                onClick={() => setOpenDeleteTable([false, null])}
                sx={{ width: "45%" }}
              >
                {t("cancel")}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default VistaStandard;
